import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";

import type { StatusRollup } from "./orders-list-model";
import { statusRollupFromCounts } from "./orders-list-model";
import {
  filledOrderLineVideos,
  isOrderLineVideoSlotEmpty,
} from "./order-line-videos";
import type {
  OrderLineVideo,
  OrderLineVideoReviewStatus,
  OrderScriptRow,
} from "../types/marketplace.types";

export type VideoFulfillmentStatusInput = Pick<
  OrderLineVideo,
  | "reviewStatus"
  | "revisionNote"
  | "isEmpty"
  | "preflightUploadId"
  | "scriptReviewId"
  | "preflightUrl"
> &
  Partial<Pick<OrderLineVideo, "id" | "position">>;

function isSameVideoSlot(
  left: VideoFulfillmentStatusInput,
  right: VideoFulfillmentStatusInput,
): boolean {
  if (left.id !== undefined && right.id !== undefined) {
    return left.id === right.id;
  }
  return left.position === right.position;
}

/** Fulfillment statuses shown on per-video rows (matches script line vocabulary). */
const VIDEO_DISPLAY_FULFILLMENT_STATUSES = new Set<MarketplaceFulfillmentStatus>([
  "SENT_TO_PRODUCTION",
  "SUBMITTED_FOR_FEEDBACK",
  "SENT_FOR_REVISION",
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "ORDER_DELIVERED",
  "CANCELLED",
  "COMPLETED_PRE_ADGE",
]);

/**
 * Maps a stored video review row to the fulfillment label partners and ops see.
 * Empty slots have no status.
 */

export function orderLineVideoFulfillmentStatus(
  scriptFulfillmentStatus: MarketplaceFulfillmentStatus,
  video: VideoFulfillmentStatusInput,
  context?: {
    /** Other videos on the same script — used for mixed revision / re-upload rows. */
    videos?: readonly VideoFulfillmentStatusInput[];
  },
): MarketplaceFulfillmentStatus | null {
  if (isOrderLineVideoSlotEmpty(video)) return null;
  if (video.reviewStatus === "REVISION_REQUESTED") {
    return "SENT_FOR_REVISION";
  }
  if (video.revisionNote?.trim()) {
    return "SENT_FOR_REVISION";
  }
  if (video.reviewStatus === "IN_REVIEW") {
    const siblings = context?.videos ?? [];
    const anotherVideoNeedsRevision = siblings.some(
      (sibling) =>
        !isSameVideoSlot(sibling, video) &&
        !isOrderLineVideoSlotEmpty(sibling) &&
        sibling.reviewStatus === "REVISION_REQUESTED",
    );
    if (anotherVideoNeedsRevision) {
      if (scriptFulfillmentStatus === "SENT_FOR_REVISION") {
        return "SUBMITTED_FOR_FEEDBACK";
      }
      if (
        scriptFulfillmentStatus === "SUBMITTED_FOR_FINAL_APPROVAL" ||
        scriptFulfillmentStatus === "SUBMITTED_FOR_FEEDBACK"
      ) {
        return "SUBMITTED_FOR_FEEDBACK";
      }
    } else if (scriptFulfillmentStatus === "SENT_FOR_REVISION") {
      return "SENT_FOR_REVISION";
    }
  }
  if (VIDEO_DISPLAY_FULFILLMENT_STATUSES.has(scriptFulfillmentStatus)) {
    return scriptFulfillmentStatus;
  }
  return scriptFulfillmentStatus;
}

function fulfillmentStatusFromReviewCount(
  scriptFulfillmentStatus: MarketplaceFulfillmentStatus,
  reviewStatus: OrderLineVideoReviewStatus,
  counts: Pick<OrderScriptRow, "videoStatusCounts">["videoStatusCounts"],
): MarketplaceFulfillmentStatus {
  if (reviewStatus === "REVISION_REQUESTED") return "SENT_FOR_REVISION";
  if (
    scriptFulfillmentStatus === "SENT_FOR_REVISION" &&
    reviewStatus === "IN_REVIEW"
  ) {
    const revisionCount = counts?.REVISION_REQUESTED ?? 0;
    if (revisionCount > 0) {
      return "SUBMITTED_FOR_FEEDBACK";
    }
    return "SENT_FOR_REVISION";
  }
  return orderLineVideoFulfillmentStatus(scriptFulfillmentStatus, {
    reviewStatus,
    revisionNote: null,
    isEmpty: false,
    preflightUploadId: "present",
    scriptReviewId: "present",
    preflightUrl: null,
  })!;
}

export function videoFulfillmentStatusChips(
  script: Pick<
    OrderScriptRow,
    "fulfillmentStatus" | "videos" | "videoStatusCounts"
  >,
): { status: MarketplaceFulfillmentStatus; count: number }[] {
  const tallies = new Map<MarketplaceFulfillmentStatus, number>();

  const videos = script.videos ?? [];
  if (videos.length > 0) {
    for (const video of videos) {
      const status =
        video.fulfillmentStatus ??
        orderLineVideoFulfillmentStatus(script.fulfillmentStatus, video, {
          videos: script.videos,
        });
      if (!status) continue;
      tallies.set(status, (tallies.get(status) ?? 0) + 1);
    }
  } else if (script.videoStatusCounts) {
    for (const reviewStatus of ["IN_REVIEW", "REVISION_REQUESTED"] as const) {
      const count = script.videoStatusCounts[reviewStatus] ?? 0;
      if (count <= 0) continue;
      const status = fulfillmentStatusFromReviewCount(
        script.fulfillmentStatus,
        reviewStatus,
        script.videoStatusCounts,
      );
      tallies.set(status, (tallies.get(status) ?? 0) + count);
    }
  }

  return [...tallies.entries()].map(([status, count]) => ({ status, count }));
}

/** Filled deliverables on this script (uploaded videos only). */
export function countFilledScriptVideos(
  script: Pick<OrderScriptRow, "videos">,
): number {
  return filledOrderLineVideos(script as OrderScriptRow).length;
}

/**
 * Script-row status when the line has at most one uploaded video — mirrors that
 * video's fulfillment label instead of a separate chip summary.
 */
export function scriptRowDisplayFulfillmentStatus(
  script: Pick<
    OrderScriptRow,
    "fulfillmentStatus" | "videos" | "videoStatusCounts"
  >,
): MarketplaceFulfillmentStatus {
  const filled = filledOrderLineVideos(script as OrderScriptRow);
  if (filled.length === 1) {
    const fromVideo =
      filled[0].fulfillmentStatus ??
      orderLineVideoFulfillmentStatus(script.fulfillmentStatus, filled[0], {
        videos: script.videos,
      });
    if (fromVideo) return fromVideo;
  }
  const counts = script.videoStatusCounts;
  if (filled.length === 0 && counts) {
    const revisionCount = counts.REVISION_REQUESTED ?? 0;
    const inReviewCount = counts.IN_REVIEW ?? 0;
    const uploadedVideoCount = revisionCount + inReviewCount;
    if (uploadedVideoCount === 1) {
      if (revisionCount === 1) return "SENT_FOR_REVISION";
      return fulfillmentStatusFromReviewCount(
        script.fulfillmentStatus,
        "IN_REVIEW",
        counts,
      );
    }
  }
  return script.fulfillmentStatus;
}

/** Parent script status when multiple uploaded videos disagree (same UX as order rollups). */
export function describeVideoStatusRollup(
  script: Pick<
    OrderScriptRow,
    "fulfillmentStatus" | "videos" | "videoStatusCounts"
  >,
): StatusRollup {
  const counts: Partial<Record<MarketplaceFulfillmentStatus, number>> = {};
  for (const chip of videoFulfillmentStatusChips(script)) {
    counts[chip.status] = chip.count;
  }
  return statusRollupFromCounts(counts, script.fulfillmentStatus);
}
